import React, { useState, useEffect } from 'react'
import { pageCache } from '../utils/pageCache'
import { useParams, useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import StoreBox, { StoreBoxPersistedProps } from '../components/StoreBox'
import { bookmarkApi } from '../api/bookmark'
import { userApi } from '../api/user'
import { toastUtil } from '../utils/toast'
import Gap from '../components/Gap'
import ConfirmDialog from '../components/ConfirmDialog'

const PlateCategoryList: React.FC = () => {
  const navigate = useNavigate()
  const { userId, categoryId, category } = useParams<{
    userId: string
    categoryId: string
    category: string
  }>()
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `plate-category-list:${userId ?? ''}:${categoryId ?? ''}`
  const [places, setPlaces] = useState<StoreBoxPersistedProps[]>(
    () => pageCache.get<StoreBoxPersistedProps[]>(cacheKey) ?? []
  )
  const [categoryName, setCategoryName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(!pageCache.has(cacheKey))
  const [isPrivate, setIsPrivate] = useState(false)
  /*
   * 이 리스트를 고칠 수 있는가 — 서버의 canEditPlatelistCategory 와 같은 규칙이다
   * (리스트 주인이거나 멤버). 화면에서만 따로 판단하면 눌리는데 서버가 거절하는
   * 경우가 생긴다. 서버가 user_id(소유자)와 is_member 를 함께 내려준다.
   */
  const [canEdit, setCanEdit] = useState(false)
  /** 길게 눌러 뺄지 묻는 중인 장소. null 이면 팝업 닫힘 */
  const [pendingRemove, setPendingRemove] =
    useState<{ id: string; name: string } | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)

  useEffect(() => {
    if (!userId) {
      toastUtil.error('사용자 정보를 불러오는데 실패했습니다')
      navigate(-1)
      return
    }

    if (!categoryId) {
      toastUtil.error('북마크 카테고리 정보를 불러오는데 실패했습니다')
      navigate(-1)
      return
    }

    if (!category) {
      toastUtil.error('카테고리 정보를 불러오는데 실패했습니다')
      navigate(-1)
      return
    }

    const loadData = async () => {
      /*
       * 캐시로 이미 그려져 있으면 로딩 화면으로 덮지 않는다.
       *
       * 덮으면 '내용 → 스피너 → 같은 내용' 으로 한 번 깜빡인다. 장소가 없는
       * 분류는 빈 문구가 떴다가 스피너가 왔다가 다시 빈 문구가 떠서 특히 눈에 띈다.
       * (자세히보기·마이·저장 시트가 이미 쓰고 있는 방식)
       */
      if (!pageCache.has(cacheKey)) {
        setIsLoading(true)
      }
      setIsPrivate(false)
      try {
        // 현재 사용자 ID 가져오기
        const currentUserResponse = await userApi.getProfile()
        const currentUserIdValue = currentUserResponse.user.id

        // 해당 북마크 카테고리의 장소 가져오기
        const response = await bookmarkApi.getBookmarksByCategory(categoryId)

        if (response.categories.length === 0) {
          setPlaces([])
          setIsLoading(false)
          return
        }

        // 북마크 카테고리명 저장
        setCategoryName(response.categories[0].name)

        // 현재 사용자와 대상 사용자가 같은지 확인
        const isOwnList = currentUserIdValue === userId

        // 길게 눌러 뺄 수 있는지 — 서버 규칙과 같게(주인 또는 멤버)
        const cat = response.categories[0]
        setCanEdit(
          String(cat.user_id ?? '') === String(currentUserIdValue) ||
            Boolean(cat.is_member)
        )

        // 백엔드가 각 장소의 category를 함께 내려주므로, 장소별 추가 호출 없이 바로 필터링한다.
        // (예전엔 장소마다 getPlace를 호출해 200개+ 리스트가 매우 느렸음)
        const filteredPlaces: StoreBoxPersistedProps[] = response.categories[0].items
          .filter((item) => item.category === category)
          .map((item) => ({
            id: item.id,
            name: item.name,
            address: item.address,
            isBookmarked: item.isBookmarked,
            hasReview: Boolean(item.has_review),
            showRemoveButton: isOwnList, // 본인 리스트일 때만 제거 버튼 표시
            // 플레이트리스트: 이 장소를 추가한 사람 프로필(아이콘 칼럼 맨 앞 아바타)
            adderNickname: item.added_by?.nickname ?? '',
            adderProfileImage: item.added_by?.profile_image ?? null,
          }))

        pageCache.set(cacheKey, filteredPlaces)
        setPlaces(filteredPlaces)
      } catch (error: any) {
        // 403 에러인 경우 비공개 플레이트리스트로 처리
        if (error.response?.status === 403) {
          setIsPrivate(true)
          setPlaces([])
        } else {
          const errorMessage =
            error.response?.data?.message ||
            '플레이트 카테고리 리스트를 불러오는데 실패했습니다'
          toastUtil.error(errorMessage)
          navigate(-1)
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [userId, categoryId, category, navigate])

  const handleRemove = (placeId: string) => {
    // 제거된 장소를 리스트에서 제거
    setPlaces((prevPlaces) =>
      prevPlaces.filter((place) => place.id !== placeId)
    )
  }

  /**
   * 길게 눌러 고른 장소를 이 리스트에서 삭제한다.
   *
   * removePlacesFromCategory 는 누가 담았든 뺀다 — 서버가 리스트 주인·멤버인지만
   * 본다. 그래서 '남이 담은 장소'도 여기서 지울 수 있다.
   *
   * 화면 목록과 돌아올 때 쓰는 캐시를 같이 고친다. 캐시를 안 고치면 스와이프로
   * 나갔다 들어왔을 때 삭제한 장소가 되살아난다.
   */
  const confirmRemove = async () => {
    if (!pendingRemove || !categoryId || isRemoving) return
    setIsRemoving(true)
    try {
      await bookmarkApi.removePlacesFromCategory(categoryId, [
        parseInt(pendingRemove.id, 10),
      ])
      setPlaces((prev) => {
        const next = prev.filter((place) => place.id !== pendingRemove.id)
        pageCache.set(cacheKey, next)
        return next
      })
      setPendingRemove(null)
      toastUtil.success('리스트에서 삭제했습니다')
    } catch (error: any) {
      toastUtil.error(
        error.response?.data?.message || '장소를 삭제하지 못했습니다'
      )
    } finally {
      setIsRemoving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
        <div className="app-container flex-1 flex flex-col overflow-hidden">
          <AppBar title={categoryName || ''} isBottomBorder />
          {/* 빈 문구와 같은 칸·같은 보정을 써야 둘이 같은 자리에 온다 */}
          <div className="flex-1 flex items-center justify-center empty-state-center">
            <LoadingSpinner size={28} borderWidth={2} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      {/* relative: 빈 안내 문구를 화면 전체 기준 상하·좌우 정가운데로 오버레이 */}
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        <AppBar title={categoryName || ''} isBottomBorder />

        {/* 장소 목록 또는 비공개 메시지 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {isPrivate ? (
            <div className="w-full h-full flex flex-col items-center justify-center px-16px">
              <img
                src="/images/lock-icon.png"
                alt="비공개"
                draggable={false}
                className={`
                  w-96px
                  h-96px
                  object-contain
                  select-none
                  [-webkit-touch-callout:none]
                `}
              />

              <Gap height={16} />

              <div className="font-pretendard font-regular text-16px leading-180% text-textColorText3 text-center">
                비공개 플레이트리스트입니다.
              </div>
            </div>
          ) : places.length > 0 ? (
            <div className="w-full flex flex-col px-20px py-16px gap-24px">
              {places.map((place) => (
                <StoreBox
                  key={place.id}
                  {...place}
                  removeCategoryId={categoryId}
                  onRemove={
                    place.showRemoveButton
                      ? () => handleRemove(place.id)
                      : undefined
                  }
                  onCardClick={(id) => {
                    if (id) {
                      navigate(`/main-map?placeId=${id}`)
                    }
                  }}
                  onLongPress={
                    canEdit
                      ? () =>
                          setPendingRemove({ id: place.id, name: place.name })
                      : undefined
                  }
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* 빈 상태: 화면 전체 기준 상하·좌우 정가운데 — 누르면 검색 화면으로 이동 */}
        {!isPrivate && places.length === 0 && (
          <div className="absolute inset-x-0 bottom-0 top-48px flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
            <button
              type="button"
              onClick={() => navigate('/search')}
              className="pointer-events-auto font-pretendard font-regular text-16px leading-180% text-textColorText2 text-center"
            >
              장소를 추가해보세요
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={pendingRemove !== null}
        message="이 장소를 리스트에서 삭제할까요?"
        confirmText="삭제"
        cancelText="취소"
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemove(null)}
      />
    </div>
  )
}

export default PlateCategoryList
